import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CustomToolTip from '../components/CustomToolTip';
import { useLanguage } from '../hooks/useLanguage';
import { useMeasurement } from '../hooks/useMeasurement';
import { convertValue, getAttributeIcon, getCorrespondingUnit, getDefaultLanguage, getUnitLabel, UNIT_CHOICES } from '../utils/lib';

/**
 * Attribute-Box for setting the value of attribute-instances.
 */
const AttributeBox = forwardRef(({ isStatic, attributeProp, removeAttribute, required = false, valueRequired = false }, ref) => {
  // do not show detailed options from the beginning, only if the type of the attribute is 'TEXT' and the value is not set
  const [showDetailedOptions, setShowDetailedOptions] = useState((attributeProp?.attribute?.type === 'TEXT' &&
    !attributeProp?.value && !valueRequired) ? false : true);
  const [attribute, setAttribute] = useState({ ...attributeProp, attribute: { ...attributeProp.attribute } });

  const defaults = useMemo(() => attributeProp.attribute.defaults[attributeProp.country] || {}, [attributeProp]);
  const validation = useMemo(() => {
    const validation = attributeProp.attribute.validation[attributeProp.country] || {};
    if (validation?.precision == null) {
      validation.precision = 3;
    }
    return validation;
  }, [attributeProp]);
  const forceUnit = useMemo(() => {
    // if the validation-unit is set, the unit should not be changeable
    return validation?.unit ? true : false;
  }, [validation]);

  const { locale } = useLanguage();

  /**
   * Props for number-input-validation.
   */
  const valueNumberInputProps = useMemo(() => {
    const valueNumberInputProps = {};
    if (attributeProp.attribute.type !== 'NUMBER') return {};
    if (validation?.min != null) {
      if (validation?.unit && attribute.unit !== validation.unit) {
        // convert min value
        valueNumberInputProps.min = convertValue(validation.min, validation.unit, attribute.unit);
      } else {
        valueNumberInputProps.min = validation.min;
      }
      valueNumberInputProps.min = valueNumberInputProps.min.toFixed(validation.precision);
    }
    if (validation?.max != null) {
      if (validation?.unit && attribute.unit !== validation.unit) {
        // convert max value
        valueNumberInputProps.max = convertValue(validation.max, validation.unit, attribute.unit);
      } else {
        valueNumberInputProps.max = validation.max;
      }
      valueNumberInputProps.max = valueNumberInputProps.max.toFixed(validation.precision);
    }
    if (validation?.step != null) {
      valueNumberInputProps.step = validation.step;
    } else {
      valueNumberInputProps.step = 0.001;
    }
    return valueNumberInputProps;
  }, [validation, defaults, attribute.unit, attributeProp]);

  /**
   * Props for unit-end-adornment.
   */
  const unitEndAdornmentInputProps = useMemo(() => {
    const obj = {};
    if (attributeProp.attribute.type !== 'NUMBER') return {};
    // add a unit to the end
    obj.endAdornment = <InputAdornment position="end">{getUnitLabel(attribute.unit, attributeProp.attribute.unitFamily)}</InputAdornment>
    return obj;
  }, [attributeProp, attribute.unit]);

  /**
   * Props for text-input-validation
   */
  const textInputProps = useMemo(() => {
    if (attributeProp.attribute.type !== 'TEXT') return {};
    const textInputProps = {};
    if (validation?.minLength != null) {
      textInputProps.minLength = validation.minLength;
    }
    if (validation?.maxLength != null) {
      textInputProps.maxLength = validation.maxLength;
    }
    if (validation?.pattern != null && validation.pattern) {
      textInputProps.pattern = validation.pattern;
    }
    return textInputProps;
  }, [validation, attributeProp]);

  const decimalRegExp = useMemo(() => {
    return new RegExp(`^-?\\d*(\\.\\d{0,${validation.precision}})?$`);
  }, [validation]);

  const intl = useIntl();

  const { type: measurementType } = useMeasurement();

  const unitSelectionValues = useMemo(() => {
    if (!attributeProp.attribute.unitFamily) {
      // return all units for the specified measurement-type
      if (measurementType === 'imperial') {
        let arr = [];
        for (let t in UNIT_CHOICES) {
          arr.push(...UNIT_CHOICES[t].filter(u => u.imperial === true || u.imperial === undefined));
        }
        return arr;
      } else {
        let arr = [];
        for (let t in UNIT_CHOICES) {
          arr.push(...UNIT_CHOICES[t].filter(u => u.imperial === false || u.imperial === undefined));
        }
        return arr;
      }
    }
    switch (attributeProp.attribute.unitFamily) {
      case 'DISTANCE':
      case 'AREA':
      case 'VOLUME':
      case 'TEMPERATURE':
      case 'WEIGHT':
        if (measurementType === 'imperial') {
          return UNIT_CHOICES[attributeProp.attribute.unitFamily].filter(x => x.imperial);
        } else {
          return UNIT_CHOICES[attributeProp.attribute.unitFamily].filter(x => !x.imperial);
        }
      default:
        return UNIT_CHOICES[attributeProp.attribute.unitFamily] || [];
    }
  }, [attributeProp, measurementType]);

  useImperativeHandle(ref, () => ({
    getData: () => {
      const obj = { ...attribute };
      return obj;
    },
  }));

  /**
   * Update attribute when changes occur in parent (mainly for locale-change).
   */
  useEffect(() => {
    setAttribute({ ...attributeProp, attribute: { ...attributeProp.attribute } });
  }, [attributeProp]);

  /**
   * This effect updates the attribute-instance's unit according to the current measurementType and converts the value, if necessary.
   */
  useEffect(() => {
    // update the unit according to the new measurement-type and update the value
    if (forceUnit) {
      // if a validation-unit is set, the unit should not be changeable
      return;
    }
    setAttribute(old => {
      const obj = { ...old };
      if (old.unit) {
        obj.unit = getCorrespondingUnit(old.unit, measurementType);
        if (old.value != null) {
          obj.value = Number.parseFloat(convertValue(old.value, old.unit, obj.unit).toFixed(validation.precision));
        }
      }
      return obj;
    });
  }, [measurementType, forceUnit]);

  const onSelectValue = (value) => {
    setAttribute(attribute => {
      switch (attribute.attribute.type) {
        case 'TEXT':
          return { ...attribute, attribute: { ...attribute.attribute }, value };
        case 'NUMBER':
          if (value != null && !decimalRegExp.test(value)) {
            // decimal not valid
            return { ...attribute };
          }
          return { ...attribute, attribute: { ...attribute.attribute }, value: value == null || value == '' ? null : Number(value) };
        case 'BOOL':
          return { ...attribute, attribute: { ...attribute.attribute }, value };
        case 'SELECTION':
          return { ...attribute, attribute: { ...attribute.attribute }, value: value !== '' ? value : null };
      }
      return attribute;
    });
  };

  const onSelectUnit = (value) => {
    setAttribute(old => {
      return { ...old, unit: value || null }
    });
  };

  /**
   * The selection-choices for type 'SELECTION'.
   */
  const selectionChoices = useMemo(() => {
    return validation?.values ? validation.values : [];
  }, [validation]);

  const textFieldStyle = {
    width: '90%'
  };

  const getValue = (v) => {
    return (v == null || v === '') ? '' : v;
  };

  const getValueFormElement = () => {
    let value;
    switch (attribute.attribute.type) {
      case 'BOOL':
        value = attribute.value;
        break;
      case 'NUMBER':
        value = attribute.value;
        break;
      case 'TEXT':
        value = attribute.value || '';
        break;
      case 'SELECTION':
        value = attribute.value || null;
        if (value !== null) {
          // now get the value from the selection-choices
          value = selectionChoices.find(choice => choice.id === value.id) || null;
        }
    }
    let inputProps = {};
    if (attribute.attribute.unitFamily) {
      // add a unit to the end
      inputProps = {
        endAdornment: (
          <InputAdornment position="end">{validation?.unit || ''}</InputAdornment>
        )
      }
    };
    switch (attribute.attribute.type) {
      case 'BOOL':
        return (
          <TextField
            id="attribute-value"
            select
            disabled={isStatic}
            sx={textFieldStyle}
            label={intl.formatMessage({ id: "AttributeBox.value" })}
            variant="standard"
            value={value ?? ''}
            onChange={(event) => onSelectValue(event.target.value)}
            InputProps={inputProps}
          >
            <MenuItem key={''} value={''} ><FormattedMessage id="AttributeBox.noValue" /></MenuItem>
            <MenuItem key={false} value={false} ><FormattedMessage id="AttributeBox.false" /></MenuItem>
            <MenuItem key={true} value={true} ><FormattedMessage id="AttributeBox.true" /></MenuItem>
          </TextField>
        );
      case 'SELECTION':
        return (
          <FormControl variant="standard" sx={textFieldStyle} >
            <InputLabel><FormattedMessage id="AttributeBox.value" /></InputLabel>
            <Select
              disabled={isStatic}
              value={value || ''}
              onChange={(event) => onSelectValue(event.target.value)}
            >
              <MenuItem value={''}><FormattedMessage id="AttributeBox.noValue" /></MenuItem>
              {
                selectionChoices.map((choice, index) => <MenuItem key={index} value={choice} >{getDefaultLanguage(choice, locale)}</MenuItem>)
              }
            </Select>
          </FormControl>
        );
      case 'NUMBER':
        return <TextField
          required={valueRequired}
          sx={textFieldStyle}
          disabled={isStatic}
          id="attribute-number-value"
          label={intl.formatMessage({ id: "AttributeBox.value" })}
          variant="standard"
          type="number"
          inputProps={valueNumberInputProps}
          InputProps={unitEndAdornmentInputProps}
          value={getValue(value)}
          onChange={event => onSelectValue(event.target.value)}
        >
        </TextField>;
      case 'TEXT':
        return <TextField
          required={valueRequired}
          sx={textFieldStyle}
          disabled={isStatic}
          id="attribute-text-value"
          label={intl.formatMessage({ id: "AttributeBox.value" })}
          variant="standard"
          type="text"
          value={value}
          onChange={event => onSelectValue(event.target.value)}
          inputProps={textInputProps}
        >
        </TextField>;
    }
  };

  return (
    <Grid item xs={12} container spacing={3} sx={{ alignItems: 'center' }} >
      <Grid item xs={4} sm={3} md={2}>
        {attribute.attribute.key}
      </Grid>
      {
        !isStatic && !showDetailedOptions &&
        <Grid item xs={2} sm={2} md={1} container sx={{ alignItems: 'center', justifyContent: 'center' }} >
          <CustomToolTip
            tooltip={<FormattedMessage id='AttributeBox.showDetailedOptions' />}
            placement={"bottom"}
          >
            <Button
              onClick={() => setShowDetailedOptions(true)}
              aria-label='show detailed options'
            >
              <AddCircleOutlineIcon fontSize='large' />
            </Button>
          </CustomToolTip>
        </Grid>
      }
      {showDetailedOptions && <Grid item xs={2} sm={2} md={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
        {getAttributeIcon(attribute.attribute.type)}
      </Grid>}
      <Grid item xs={3} sm={3} md={2} style={{ visibility: showDetailedOptions ? 'visible' : 'hidden' }} >
        {getValueFormElement()}
      </Grid>
      {
        <Grid item xs={3} sm={3} md={2} style={{ visibility: showDetailedOptions && attribute.attribute.type === 'NUMBER' && !forceUnit ? 'visible' : 'hidden' }} >
          <FormControl variant="standard" sx={textFieldStyle} >
            <InputLabel id="selected-number-unit-label" ><FormattedMessage id="EditAttribute.unit" /></InputLabel>
            <Select
              required={attribute.attribute.unitFamily != null}
              labelId="selected-number-unit-label"
              disabled={isStatic}
              value={attribute.unit || ''}
              onChange={event => onSelectUnit(event.target.value)}
              label={<FormattedMessage id="EditAttribute.unit" />}
            >
              {
                !attribute.attribute.unitFamily && <MenuItem value={''}><FormattedMessage id="EditAttribute.none" /></MenuItem>
              }
              {
                unitSelectionValues.map(({ value, label }, index) => <MenuItem key={index} value={value} >{label}</MenuItem>)
              }
            </Select>
          </FormControl>
        </Grid>
      }

      <Grid item xs={2} sm={2} md={2} container alignItems={'center'}>
        {!isStatic && !required && <Button
          variant="outlined"
          onClick={removeAttribute}
          color="error"
          aria-label="delete"
        >
          <DeleteIcon />
        </Button>}
      </Grid>
    </Grid>
  );
});

export default AttributeBox;